import React, { useEffect, useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import CreateTestCaseForm from '../CreateTestCaseForm';
import { getTestCaseData } from '../../../../modules/config';
import { updateTestCases } from '../../../../modules/jira';
import { getAssociatedUsers, getCurrentProject } from '../../../../modules/project';
import { useNotification } from '../../../../hooks/NotificationContext';
import { Button } from '@mui/material';
import { isExecutiveManager } from '../../../../lib/userUtils';

const TestCaseDetails = ({
  data,
  fetchTestCaseData,
  testCaseId,
  projectId,
}) => {
  const defaultFormData = {
    objective: data?.objective || '',
    summary: data?.summary || '',
    preCondition: data?.pre_condition || '',
    labels: data?.labels || [],
    testsuites: data?.test_suites || [],
    priority: data?.priority?.id || null,
    status: data?.status?.id || null,
    module: data?.module?.id || null,
    owner: data?.owner || '',
    estimatedTime: data?.estimated_time || '',
    isAutomated: data?.is_automated || 'No',
    reasonForNotAutomated: data?.reason_for_not_automated || '',
    technologyLayer: data?.technology_layer || '',
    reviewer: data?.reviewer || '',
    approvedBy: data?.approved_by || '',
    automatedBy: data?.automated_by || '',
    automationMethod: data?.automation_method || null,
    automationStatus: data?.automation_status || null,
    isAutomationNeeded: data?.is_automation_needed || false,
    country: data?.country || [],
    automationCountry: data?.automation_country || [],
    notAutomatedCategory: data?.not_automated_category || null,
    notAutomatedSubCategory: data?.not_automated_sub_category || null,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [isFormEdited, setIsFormEdited] = useState(false);
  const { addNotification } = useNotification();
  const [statuses, setStatuses] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [modules, setModules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [isReviewWorkflow, setIsReviewWorkflow] = useState(false);

  const fetchProjectData = async () => {
    const response = await getCurrentProject(projectId);
    setIsReviewWorkflow(response.review_workflow);
  };
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(defaultFormData);
  };

  const getTestCasesData = async () => {
    setLoading(true);
    try {
      const response = await getTestCaseData(projectId);

      if (response) {
        const fetchedStatuses = response?.result.filter(
          (item) => item.group === 'TEST_CASE_STATUS'
        );
        const fetchedPriorities = response?.result.filter(
          (item) => item.group === 'TEST_CASE_PRIORITY'
        );
        const fetchedLabels = response?.result.filter(
          (item) => item.group === 'LABEL'
        );
        const fetchedModules = response?.result.filter(
          (item) => item.group === 'FUNCTIONAL_MODULE_FIELD'
        );
        const testSuitesResponse = response?.test_suites;
        setTestSuites(testSuitesResponse);
        setStatuses(fetchedStatuses);
        setPriorities(fetchedPriorities);
        setLabels(fetchedLabels);
        setModules(fetchedModules);
      }
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setProjectUsers(
          usersResponse.map((userData) => ({
            ...userData.user,
            role: userData.role,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching test case data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = async () => {
    if (!formData.summary.trim()) {
      addNotification('danger', 'Please add description');
      return;
    }
    try {
      const testcase = {
        ...formData,
        reasonForNotAutomated:
          formData.isAutomated === 'No'
            ? (formData.reasonForNotAutomated || '').trim()
            : '',
      };
      await updateTestCases({ testcase }, testCaseId);

      addNotification('success', 'Test case updated successfully!');
      fetchTestCaseData();
      setIsEditing(false);
      setIsFormEdited(false);
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to update test case. Please try again later.';
      addNotification('danger', errorMessage);
    }
  };

  useEffect(() => {
    getTestCasesData();
    fetchProjectData();
  }, []);

  const disableSave =
  formData.summary === '' ||
  !isFormEdited ||
  formData.testsuites.length === 0 || 
  (formData.isAutomated === 'Yes' && formData?.automationMethod === null) || 
  (formData.isAutomationNeeded === true && formData.isAutomated === 'No' && 
    (formData?.notAutomatedCategory === null || formData?.notAutomatedCategory === '' || 
      formData?.notAutomatedSubCategory === null || formData?.notAutomatedSubCategory === ''));

  return (
    <Container fluid className="px-0">
      <Card className="shadow-sm mb-4">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ height: '50px' }}>
          <h5 className="mb-0">Test Case Details</h5>
          {isEditing ? (
            <div>
              <Button
                variant="contained"
                onClick={handleSaveClick}
                title={isFormEdited ? 'Save changes' : 'No changes to save'}
                disabled={disableSave}>
                Save
              </Button>
              <Button
                style={{ marginLeft: '.5rem' }}
                variant="secondary"
                onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          ) : (
            !isExecutiveManager() && !data?.is_archived && (
              <Button variant="contained" onClick={handleEditClick}>
                Edit
              </Button>
            )
          )}
        </Card.Header>
        <Card.Body>
          <CreateTestCaseForm
            formData={formData}
            isEditing={isEditing}
            setFormData={setFormData}
            defaultFormData={defaultFormData}
            setIsFormEdited={setIsFormEdited}
            isFormEdited={isFormEdited}
            testSuites={testSuites}
            projectUsers={projectUsers}
            statuses={statuses}
            priorities={priorities}
            labels={labels}
            modules={modules}
            loading={loading}
            isReviewWorkflow={isReviewWorkflow}
            refetchData={getTestCasesData}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TestCaseDetails;
